import {
  CATEGORIES,
  CONSENT_COOKIE_NAME,
  CONSENT_MAX_AGE_SECONDS,
  OPT_IN_CATEGORIES,
  POLICY_VERSION,
  SCHEMA_VERSION,
} from './constants';
import { browserCookies } from './cookieAdapter';

function readRawCookie(jar, name) {
  const entries = String(jar || '').split(';');
  for (const entry of entries) {
    const trimmed = entry.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.slice(name.length + 1));
    }
  }
  return null;
}

function newConsentId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Node 18+ and every browser we support expose crypto.randomUUID; this branch
  // exists only so a missing crypto never throws during consent capture.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0;
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}

function normalizeCategories(input) {
  const categories = { [CATEGORIES.NECESSARY]: true };
  for (const category of OPT_IN_CATEGORIES) {
    categories[category] = Boolean(input?.[category]);
  }
  return categories;
}

export function createConsentStore(cookies = browserCookies) {
  let snapshot;
  let loaded = false;
  const listeners = new Set();

  function parse() {
    const raw = readRawCookie(cookies.get(), CONSENT_COOKIE_NAME);
    if (!raw) {
      return null;
    }

    let parsed;
    try {
      parsed = JSON.parse(raw);
    }
    catch {
      // A corrupt cookie is indistinguishable from no consent, and must never
      // be treated as permission.
      return null;
    }

    // Version drift invalidates stored consent: the visitor agreed to a
    // different document, or a different payload shape, than the one we ship.
    if (parsed?.schemaVersion !== SCHEMA_VERSION || parsed?.policyVersion !== POLICY_VERSION) {
      return null;
    }

    // Carrying the right versions is not enough: without the fields that
    // identify *which* decision was made, this is not a decision. Returning it
    // anyway would set hasDecided and suppress the banner, leaving a visitor
    // who was never asked with no stored consent and no prompt.
    if (typeof parsed.consentId !== 'string' || parsed.consentId === ''
      || typeof parsed.timestamp !== 'number' || !Number.isFinite(parsed.timestamp)) {
      return null;
    }

    return {
      schemaVersion: parsed.schemaVersion,
      policyVersion: parsed.policyVersion,
      consentId: parsed.consentId,
      timestamp: parsed.timestamp,
      categories: normalizeCategories(parsed.categories),
    };
  }

  function load() {
    if (!loaded) {
      snapshot = parse();
      loaded = true;
    }
    return snapshot;
  }

  function persist(state) {
    const value = encodeURIComponent(JSON.stringify(state));
    cookies.set(
      `${CONSENT_COOKIE_NAME}=${value}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; Path=/; SameSite=Lax; Secure`,
    );
    snapshot = state;
    loaded = true;
    for (const listener of listeners) {
      listener();
    }
    return state;
  }

  // Declared as closures rather than `this`-dependent methods: useConsent and
  // useSyncExternalStore pass these around as bare references, where `this`
  // would be undefined.
  function has(category) {
    if (category === CATEGORIES.NECESSARY) {
      return true;
    }
    return Boolean(load()?.categories?.[category]);
  }

  function save(categories) {
    const existing = load();
    return persist({
      schemaVersion: SCHEMA_VERSION,
      policyVersion: POLICY_VERSION,
      consentId: existing?.consentId || newConsentId(),
      timestamp: Date.now(),
      categories: normalizeCategories(categories),
    });
  }

  function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  return {
    read: load,
    getSnapshot: load,
    has,
    save,
    subscribe,
    withdraw: () => save({}),
  };
}

export const consentStore = createConsentStore();
